import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { catchError, map, Observable, of, take, tap } from 'rxjs';

import { ibanFormatter } from '@ap/formatters';
import { RekeningvalidatieResource, RekeningvalidatieResultaat } from '@ap/interface-apic-rekeningvalidatie';
import { HttpResponse } from '@ap/interface-generic';

export class ApIbanValidator {
    private static readonly IBAN_PATTERN = /^[A-Za-z]{2}[0-9]{2}[A-Za-z0-9]{11,30}$/i;
    private static readonly previousValidationMap = new Map<AbstractControl, unknown>();
    private static readonly previousValuesMap = new Map<AbstractControl, unknown>();

    public static isIban(iban: string): boolean {
        return !!iban && ApIbanValidator.IBAN_PATTERN.test(iban.replace(/\s/g, ''));
    }

    public static createValidator(rekeningvalidatieResource: RekeningvalidatieResource, allowNonSepa: boolean): AsyncValidatorFn {
        return (control: AbstractControl) => {
            return ApIbanValidator.validate(control, rekeningvalidatieResource, allowNonSepa);
        };
    }

    public static validate(
        control: AbstractControl,
        rekeningvalidatieResource: RekeningvalidatieResource,
        allowNonSepa: boolean
    ): Observable<ValidationErrors> {
        if (ApIbanValidator.previousValuesMap.get(control) !== control.value) {
            ApIbanValidator.previousValuesMap.set(control, control.value);
            return ApIbanValidator.handleLogic(control, rekeningvalidatieResource, allowNonSepa).pipe(
                tap((response) => {
                    ApIbanValidator.previousValidationMap.set(control, response);
                })
            );
        }

        return of(ApIbanValidator.previousValidationMap.get(control));
    }

    public static handleLogic(control, rekeningvalidatieResource, allowNonSepa: boolean) {
        const iban: string = control.value?.replace(/ /g, '');

        if (!iban) {
            return of(null);
        }

        // this regex is deliberately different from the general IBAN regex
        // used by ap-creditor
        // see https://devolksbank.atlassian.net/browse/MO2-5475
        if (!/^[a-zA-Z]{2}[0-9][a-zA-Z0-9]+$/.test(iban)) {
            return allowNonSepa ? of(null) : of({ nonSepa: true });
        }

        if (!ApIbanValidator.isIban(iban)) {
            return of({ iban: true });
        }
        return rekeningvalidatieResource.getRekeningvalidatieResultaat({ rekeningnummer: ibanFormatter.format(iban) }).pipe(
            map((result: RekeningvalidatieResultaat) => {
                const res: any = {};

                if (!allowNonSepa && !result.sepa_land) {
                    res.nonSepa = true;
                }
                if (!result.geldige_samenstelling) {
                    res.composition = true;
                }
                return res.nonSepa || res.composition ? res : null;
            }),
            take(1),
            catchError((error) => (error.status === HttpResponse.BAD_REQUEST ? of({ composition: true }) : of(null)))
        );
    }
}
